import { useState, useEffect, useRef } from 'react';
import { styled } from '@mui/material/styles';
import { useAppSelector } from '../../store/hooks';

import DialActions from '../draw/DialActions';
import { RGBtoHex } from '../draw/helpers';

import bitxel from './img/bitxel.png';

const ImportCanvas = styled('canvas')({
  position: 'absolute',
  top: 0,
  left: 0,
  width: 301,
  height: 301,
});

const SmallScreenText = styled('p')({
  color: '#54a936',
  fontWeight: 800,
  textAlign: 'center',
  border: '1px solid hsla(230,8%,60%,.8)',
  borderRadius: 10,
  margin: '-20px 10px 0',
  whiteSpace: 'pre-line',
  padding: '10px 5px'
});

type PixelData = [number, number, number];
type AssetLayer = [string, PixelData[]];

export default function Import() {
  const width = window.innerWidth;
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [canvas, setCanvas] = useState<HTMLCanvasElement | null>(null);
  const [context, setContext] = useState<CanvasRenderingContext2D | null>(null);
  const [scannedJson, setScannedJson] = useState<string>('');

  const looking = useAppSelector((state) => state.draw.looking);

  const matrixRef = useRef<(string | undefined)[]>([]);

  useEffect(() => {
    setCanvas(canvasRef.current);
    if (canvas !== null) {
      setContext(canvas.getContext('2d'));
    }
  }, [canvas]);

  useEffect(() => {
    if (context !== null && canvas !== null) {
      const image = new Image();
      image.src = bitxel;
      image.onload = () => {
        context.drawImage(image, 0, 0, canvas.width, canvas.height);
        const matrix: (string | undefined)[] = [];
        for (let i = 1; i <= 43; i++) {
          for (let j = 1; j <= 43; j++) {
            const data = context.getImageData(j * 7, i * 7, 7, 7).data;
            const color = '#' + RGBtoHex(data[0], data[1], data[2]);
            matrix.push(!data[3] ? undefined : color);
          }
        }
        matrixRef.current = matrix;
      };
    }
  }, [context, canvas]);

  const draw = (M: AssetLayer | AssetLayer[] | undefined) => {
    if (!M || !context || !canvas) return;

    if (Array.isArray(M) && typeof M[0] !== 'string') {
      for (const item of M as AssetLayer[]) {
        draw(item);
      }
    } else {
      const layer = M as AssetLayer;
      const color = layer[0];
      const pixels = layer[1];

      for (const pixelData of pixels) {
        let pixelWidth = 7;
        let pixelHeight = 7;
        let Mm: number;

        if (Array.isArray(pixelData)) {
          Mm = pixelData[0];
          pixelWidth = pixelData[1] * 7;
          if (pixelData[2]) pixelHeight = pixelData[2] * 7;
        } else {
          Mm = pixelData as unknown as number;
        }

        let posy = Mm / 43;
        const pyrn = Math.round(posy);
        let posx = Mm - (43 * pyrn);
        posx = ((posx < 0) ? 43 + posx : posx) * 7;
        posy = (pyrn - ((posy < pyrn) ? 1 : 0)) * 7;

        if (looking) {
          posx = canvas.width - posx;
          pixelWidth *= -1;
        }

        context.fillStyle = color || '#FF0000';
        context.fillRect(posx, posy, pixelWidth, pixelHeight);
      }
    }
  };

  const processImage = () => {
    const matrix = matrixRef.current;
    const fixd: Record<string, PixelData[]> = {};

    for (let m = 0; m < matrix.length; m++) {
      if (matrix[m]) {
        const color = matrix[m]!;
        if (!fixd[color]) fixd[color] = [];

        const fxm = fixd[color];
        const lastItem = fxm[fxm.length - 1];

        if (lastItem) {
          const difx = Math.abs(lastItem[0] + lastItem[1] - 1 - m);
          const dify = Math.abs(lastItem[0] - m) - 43 * lastItem[2];

          if (difx === 1 && lastItem[2] === 1) {
            lastItem[1] += 1;
          } else if (dify === 0 && lastItem[1] === 1) {
            lastItem[2] += 1;
          } else {
            fixd[color].push([m, 1, 1]);
          }
        } else {
          fixd[color].push([m, 1, 1]);
        }
      }
    }

    const melt = (F: PixelData[]): PixelData[] => {
      let xy = 0;
      while (xy < 2) {
        for (let f = 0; f < F.length; f++) {
          for (let ff = 0; ff < F.length; ff++) {
            if (ff === f) continue;
            if (xy === 0) {
              const subs = F[f][0] + 43 * F[f][2] - F[ff][0];
              const x = F[f][1] === F[ff][1];
              if (subs === 0 && x) {
                F[f][2] += 1;
                F.splice(ff, 1);
                return melt(F);
              }
            } else {
              if (F[f][1] === 1 && !((F[f][0] + 1) % 43)) continue;
              const near = (F[f][0] + F[f][1] - F[ff][0]) === 0;
              const y = F[f][2] === F[ff][2];
              if (near && y) {
                F[f][1] += 1;
                F.splice(ff, 1);
                return melt(F);
              }
            }
          }
        }
        xy++;
      }
      return F;
    };

    for (const f in fixd) {
      fixd[f] = melt(fixd[f]);
    }

    let json = '[\n';
    for (const f in fixd) {
      json += `  ["${f.toUpperCase()}", [`;
      for (let ff = 0; ff < fixd[f].length; ff++) {
        const item = fixd[f][ff];
        const adjustedPos = item[0] + 44;
        json += `[${adjustedPos},${item[1]},${item[2]}]`;
        if (ff < fixd[f].length - 1) json += ',';
      }
      json += ']],\n';
    }
    json = json.slice(0, -2) + '\n]';

    setScannedJson(json);

    try {
      const scannedAsset = JSON.parse(json) as AssetLayer[];
      console.log('json: ', json);
      console.log('scannedAsset: ', scannedAsset);

      if (context && canvas) {
        context.clearRect(0, 0, canvas.width, canvas.height);
        draw(scannedAsset);
      }
    } catch (e) {
      console.error('Error parsing JSON:', e);
    }
  };

  const copyToClipboard = () => {
    if (scannedJson) {
      navigator.clipboard.writeText(scannedJson);
      console.log('Copied to clipboard!');
    }
  };

  if (width <= 310) {
    return (
      <SmallScreenText>
        Unfortunately your device is too small to support 8bitpix.
        <br /><br />
        Try on desktop or using a larger screen.
      </SmallScreenText>
    );
  }

  return (
    <>
      <div className="container">
        <div className="importer" style={{ position: 'relative', zIndex: 100 }}>
          <ImportCanvas ref={canvasRef} width={301} height={301} />
        </div>
      </div>
      <div style={{ marginTop: '350px' }}>
        {scannedJson && (
          <pre style={{
            fontSize: '10px',
            maxHeight: '400px',
            overflow: 'auto',
            background: '#1a1a1a',
            padding: '10px',
            color: '#fff',
            borderRadius: '8px',
            margin: '10px'
          }}>
            {scannedJson}
          </pre>
        )}
      </div>
      <DialActions save={processImage} share={copyToClipboard} />
    </>
  );
}
